import React from "react";
import { Reveal } from "../components/Reveal";
import { AWWatermark } from "../components/AWWatermark";
import { buildWhatsAppLink, EMAIL, PHONE_DISPLAY, WHATSAPP_NUMBER } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";
import { MapPin, Clock, MessageCircle, ArrowRight, Mail, Phone } from "lucide-react";

const Contact = () => {
  const { t, images } = useLanguage();
  usePageMeta(t.meta.contact);

  return (
    <div data-testid="contact-page" className="bg-white">
      {/* Header */}
      <section className="pt-40 md:pt-48 pb-20 md:pb-28 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
              — {t.contact.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-aw text-5xl md:text-6xl lg:text-7xl font-light tracking-tight max-w-4xl leading-[1.05]">
              {t.contact.heading}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-sans-aw text-base md:text-lg text-black/70 leading-relaxed mt-8 max-w-2xl">
              {t.contact.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Info grid + image */}
      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          <Reveal className="md:col-span-7" delay={0.1}>
            <img
              src={images.zurich}
              loading="lazy"
              alt="Zürich"
              className="w-full h-[460px] md:h-[640px] object-cover aw-grayscale"
            />
          </Reveal>
          <div className="md:col-span-5 space-y-12">
            <Reveal delay={0.2}>
              <div data-testid="contact-address" className="border-t border-black pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin size={16} strokeWidth={1.25} />
                  <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50">
                    {t.contact.address_title}
                  </p>
                </div>
                <address className="not-italic font-serif-aw text-xl md:text-2xl text-black leading-relaxed">
                  {t.contact.address_lines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </address>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div data-testid="contact-hours" className="border-t border-black pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={16} strokeWidth={1.25} />
                  <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50">
                    {t.contact.hours_title}
                  </p>
                </div>
                <ul className="font-sans-aw text-base md:text-lg text-black/80 leading-relaxed space-y-1">
                  {t.contact.hours_lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.4}>
              <div data-testid="contact-direct" className="border-t border-black pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <MessageCircle size={16} strokeWidth={1.25} />
                  <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50">
                    {t.contact.contact_title}
                  </p>
                </div>
                <a
                  data-testid="contact-whatsapp-cta"
                  href={buildWhatsAppLink(t.whatsapp.default_message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-aw btn-aw-dark font-sans-aw text-[11px] tracking-luxury uppercase px-8 py-4 inline-flex items-center gap-3 mt-2"
                >
                  {t.contact.whatsapp_cta}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </a>
                <div className="mt-6 space-y-3 font-sans-aw text-sm">
                  <a data-testid="contact-phone" href={`tel:+${WHATSAPP_NUMBER}`} className="flex items-center gap-3 text-black hover:opacity-70 transition-opacity">
                    <Phone size={14} strokeWidth={1.25} />
                    {PHONE_DISPLAY}
                  </a>
                  <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-black hover:opacity-70 transition-opacity break-all">
                    <Mail size={14} strokeWidth={1.25} />
                    {EMAIL}
                  </a>
                </div>
                <p className="font-sans-aw text-xs text-black/50 mt-4 max-w-xs">
                  {t.contact.whatsapp_note}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Closing band */}
      <section className="relative bg-[#fafafa] py-28 md:py-40 px-6 md:px-12 overflow-hidden">
        <AWWatermark size={580} opacity={0.04} position="right" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/40 mb-6">
              Atelier · Zürich
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-serif-aw text-3xl md:text-5xl font-light italic leading-tight">
              {t.closing.contact_quote}
            </h2>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
